import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import type { SeedsMap } from "@/types/creativesphere";

import { blueprintQueryOptions } from "@/api/creativesphere";
import { buildPilotGenerateRequest } from "@/api/creativesphere/buildGenerateRequest";
import {
  productDetailsFromIngested,
  productSeedMessage,
  type ProductDetails,
} from "@/api/creativesphere/productSeed";
import {
  ingestedProductQueryOptions,
  searchProducts,
  useInfiniteListProductCards,
} from "@/api/products";
import { AcceptPilotModal } from "@/features/Generations/components/AcceptPilotModal";
import { BlueprintPickerModal } from "@/features/Generations/components/BlueprintPickerModal";
import { BlueprintPickerStep } from "@/features/Generations/components/BlueprintPickerStep";
import { GenerationsStepper } from "@/features/Generations/components/GenerationsStepper";
import { ProductPickerModal } from "@/features/Generations/components/ProductPickerModal";
import { PilotCanvas } from "@/features/Generations/components/SetupStep/PilotCanvas";
import { SetupRail } from "@/features/Generations/components/SetupStep/SetupRail";
import { usePilotMutations } from "@/features/Generations/components/SetupStep/usePilotMutations";
import {
  usePilotState,
  type PilotState,
} from "@/features/Generations/context/PilotStateContext";
import { productCardsFilterForSelectedGroup } from "@/features/Generations/pilotFlowGroup";
import { composePropagationName } from "@/features/Generations/propagationList";
import { generationsStepRoutePath } from "@/features/Generations/steps";
import { hydrateGroupProducts } from "@/features/Generations/utils/hydrateGroupProducts";
import { startedFromForOrigin } from "@/features/Generations/utils/pilotFromPropagation";
import { PROPAGATION_STATE, type ProductRef } from "@/types/propagation";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

export type SetupStepProps = {
  siteId: string;
};

// Any product/blueprint change invalidates the stored pilot until the next run.
function withPilotInput(
  prev: PilotState,
  patch: Partial<Pick<PilotState, "product" | "blueprint">>,
): PilotState {
  return {
    ...prev,
    ...patch,
    pilotInputsChanged: Boolean(prev.propagationSlug),
    generationSlug: undefined,
    pilotImageUrl: undefined,
  };
}

export function SetupStep({ siteId }: SetupStepProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { pilotState, setPilotState } = usePilotState();
  const { blueprint, origin } = pilotState;
  const { groupType, groupName } = useSearch({ strict: false });
  // Mutation callbacks compare against the live state, not their render's.
  const pilotStateRef = useRef(pilotState);
  useEffect(() => {
    pilotStateRef.current = pilotState;
  });

  const [picking, setPicking] = useState(false);
  const showPicker = picking || (!blueprint && origin === "blueprint");
  const [pickingProduct, setPickingProduct] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [pickingBlueprint, setPickingBlueprint] = useState(false);
  const [isFetchingBlueprint, setIsFetchingBlueprint] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);

  const group = pilotState.productGroup;
  // The accept modal stays closable while acceptPilot awaits, and any selection change clears the slug.
  const acceptInputs = {
    open: accepting,
    slug: pilotState.propagationSlug,
    productId: pilotState.product?.id,
    blueprintSlug: blueprint?.slug,
    group,
  };
  const acceptInputsRef = useRef(acceptInputs);
  useEffect(() => {
    acceptInputsRef.current = acceptInputs;
  });
  const {
    data: groupCards,
    isError: hasGroupCardsError,
    error: groupCardsError,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingGroupCards,
    fetchNextPage,
  } = useInfiniteListProductCards(siteId, {
    enabled: Boolean(group),
    filter: group ? productCardsFilterForSelectedGroup(group) : undefined,
  });

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const groupProducts =
    groupCards?.pages.flatMap((page) => page.products) ?? [];

  // Propagation is all-or-nothing: the request carries an explicit product
  // list, so accepting mid-paging would silently propagate across only the
  // pages loaded so far. Block acceptance until the loop above has drained
  // every page.
  const isLoadingCollection =
    Boolean(group) &&
    (isLoadingGroupCards || hasNextPage || isFetchingNextPage);

  // Derived from the slug so remounts (e.g. back to starting-point and return)
  // keep the CTA in sync with the propagation the provider already holds. A
  // product/blueprint change flags the stored pilot stale until the next run.
  const hasGenerated =
    Boolean(pilotState.propagationSlug) &&
    !pilotState.pilotInputsChanged &&
    pilotState.pilotRunState !== "failed";
  // A reopened story still rendering, with no generation to poll: hold the CTA
  // until the row settles rather than offering to accept an unfinished pilot.
  const isResumingGeneration =
    pilotState.pilotRunState === "generating" && !pilotState.generationSlug;

  const {
    createPilot,
    isCreatingPilot,
    generatePilot,
    isGeneratingPilot,
    propagateCollection,
    updatePilot,
    isUpdatingPilot,
    updatePropagationState,
  } = usePilotMutations(siteId, setPilotState);

  const isPilotRequestPending =
    isCreatingPilot || isUpdatingPilot || isGeneratingPilot;
  const isBusy = isPilotRequestPending || isFetchingBlueprint;

  // The pilot's seeds come from GET /blueprints/{id}, not from the list item.
  // `retry: false` keeps a failing blueprint from stalling the click behind the
  // global 3-retry backoff — the error surfaces immediately instead.
  const loadBlueprintSeedsMap = async (
    blueprintSlug: string,
  ): Promise<SeedsMap> => {
    const blueprintResponse = await queryClient.fetchQuery({
      ...blueprintQueryOptions(siteId, blueprintSlug),
      retry: false,
    });
    // The propagator builds the product seed per item.
    return Object.fromEntries(
      Object.entries(blueprintResponse.seeds_map).filter(
        ([key]) => key !== "product",
      ),
    );
  };

  const runGeneration = async () => {
    const { product } = pilotState;
    if (!product || !blueprint) return;
    setIsFetchingBlueprint(true);
    let seedsMap: SeedsMap;
    let details: ProductDetails;
    try {
      seedsMap = await loadBlueprintSeedsMap(blueprint.slug);
      // The click can beat PilotProductDetails' hydration; same query key, so an in-flight fetch is deduped.
      details = product.details_loaded
        ? product
        : await queryClient
            .fetchQuery({
              ...ingestedProductQueryOptions(siteId, product.id),
              retry: false,
            })
            .then(productDetailsFromIngested)
            .catch(() => ({}));
    } catch (error) {
      enqueueErrorSnackbar(error, "Failed to get blueprint. Please try again.");
      return;
    } finally {
      setIsFetchingBlueprint(false);
    }
    // All Products launches have no group handle; the URL's groupName
    // ("All Products") stands in so the story still reads as a pair.
    const name = composePropagationName(
      group?.handle || groupName,
      blueprint.name,
    );
    // Ignore a response for a product/blueprint the user has since changed.
    const stillCurrent = (prev: PilotState) =>
      prev.product?.id === product.id &&
      prev.blueprint?.slug === blueprint.slug;

    const existingSlug = pilotState.propagationSlug;
    // The propagation is created or updated once the generate call returns,
    // so its slug can travel on the request that produced the pilot.
    generatePilot(
      buildPilotGenerateRequest({
        siteId,
        seedsMap: {
          ...seedsMap,
          product: productSeedMessage({
            title: product.title,
            image_url: product.image_url,
            description: details.description,
            category: details.category,
          }),
        },
        productImageUrl: product.image_url,
        groupId: group?.group_id,
        groupType: group?.group_type,
        propagationId: existingSlug,
      }),
      {
        onSuccess: (generation) => {
          // Inputs changed while the request was in flight: drop this run so
          // the abandoned product is never written to the row.
          if (!stillCurrent(pilotStateRef.current)) return;
          // The canvas polls this slug for the render.
          setPilotState((prev) => ({
            ...prev,
            generationSlug: generation.slug,
            pilotRunState: undefined,
          }));
          // The response rarely carries the asset yet; PilotCanvas stores the
          // final render once polling settles.
          const renderedUrl = generation.assets[0]?.url;
          const pilotFields = {
            seeds_map: seedsMap,
            ...(name ? { name } : {}),
            from_blueprint_id: blueprint.slug,
            blueprint_id: blueprint.slug,
            pilot_vendor_product_id: product.id,
            ...(renderedUrl ? { pilot_image_url: renderedUrl } : {}),
            ...(group
              ? {
                  group_type: group.group_type,
                  ...(group.group_id ? { group_id: group.group_id } : {}),
                }
              : {}),
          };
          if (existingSlug) {
            updatePilot({
              slug: existingSlug,
              body: {
                ...pilotFields,
                propagation_state: PROPAGATION_STATE.SETUP,
              },
            });
            return;
          }
          createPilot(
            {
              ...pilotFields,
              started_from: startedFromForOrigin(origin),
              propagation_state: PROPAGATION_STATE.SETUP,
            },
            {
              onSuccess: (propagation) =>
                setPilotState((prev) =>
                  stillCurrent(prev)
                    ? { ...prev, propagationSlug: propagation.slug }
                    : prev,
                ),
            },
          );
        },
      },
    );
    setPickingProduct(false);
    setPickingBlueprint(false);
  };

  const goToStartingPoint = () =>
    void navigate({
      to: generationsStepRoutePath("starting-point"),
      params: { siteId, stepId: "starting-point" },
    });

  const goToAcceptCollection = () =>
    void navigate({
      to: generationsStepRoutePath("accept-collection"),
      params: { siteId, stepId: "accept-collection" },
      search: {
        propagationId: pilotState.propagationSlug,
        ...(groupType ? { groupType } : {}),
        ...(groupName ? { groupName } : {}),
      },
    });

  const openAcceptPilot = () => {
    const slug = pilotState.propagationSlug;
    if (slug) {
      updatePropagationState({
        slug,
        body: {
          propagation_state: PROPAGATION_STATE.PILOT_ACCEPTED,
        },
      });
    }
    setAccepting(true);
  };

  const acceptPilot = async () => {
    if (isAccepting) return;
    const slug = pilotState.propagationSlug;
    if (!slug || !group) {
      setAccepting(false);
      goToAcceptCollection();
      return;
    }
    if (hasGroupCardsError) {
      enqueueErrorSnackbar(
        groupCardsError,
        "Failed to load the collection. Please try again.",
      );
      return;
    }
    // Backstop for the disabled CTA — never propagate a partial product list.
    if (isLoadingCollection) {
      enqueueErrorSnackbar(
        undefined,
        "Still loading the collection. Please try again in a moment.",
      );
      return;
    }
    setIsAccepting(true);
    try {
      const product = pilotState.product;
      if (!blueprint || !product) {
        setAccepting(false);
        goToAcceptCollection();
        return;
      }
      let seedsMap: SeedsMap;
      try {
        seedsMap = await loadBlueprintSeedsMap(blueprint.slug);
      } catch (error) {
        enqueueErrorSnackbar(
          error,
          "Failed to get blueprint. Please try again.",
        );
        return;
      }
      let products: ProductRef[];
      try {
        products = await hydrateGroupProducts(groupProducts, (productIds) =>
          searchProducts(siteId, { product_ids: productIds }),
        );
      } catch (error) {
        enqueueErrorSnackbar(
          error,
          "Failed to load product details. Please try again.",
        );
        return;
      }
      const now = acceptInputsRef.current;
      if (
        !now.open ||
        now.slug !== slug ||
        now.productId !== product.id ||
        now.blueprintSlug !== blueprint.slug ||
        now.group !== group
      ) {
        return;
      }
      const productGroup = {
        group_type: group.group_type,
        ...(group.group_id ? { group_id: group.group_id } : {}),
        ...(group.handle ? { handle: group.handle } : {}),
        products,
      };
      updatePropagationState({
        slug,
        body: {
          propagation_state: PROPAGATION_STATE.PROPAGATING,
        },
      });

      propagateCollection({
        site_id: siteId,
        blueprint_id: blueprint.slug,
        propagation_slug: slug,
        product_group: productGroup,
        seeds_map: seedsMap,
      });
      setAccepting(false);
      goToAcceptCollection();
    } finally {
      setIsAccepting(false);
    }
  };

  if (showPicker) {
    return (
      <BlueprintPickerStep
        siteId={siteId}
        onSelect={(selected) => {
          setPilotState((prev) =>
            withPilotInput(prev, { blueprint: selected }),
          );
          setPicking(false);
        }}
        onBack={() => {
          if (blueprint) setPicking(false);
          else goToStartingPoint();
        }}
      />
    );
  }

  const hasProduct = Boolean(pilotState.product?.id);
  const hasScene = Boolean(blueprint);
  const canGenerate = hasProduct && hasScene;
  const primaryLabel =
    isBusy || isResumingGeneration
      ? "Generating…"
      : hasGenerated
        ? isLoadingCollection
          ? "Loading collection…"
          : "Accept Pilot"
        : "Run generation";

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
        {/* Progress bar starts here, once a blueprint puts the user on the canvas. */}
        <GenerationsStepper currentStepId="setup" />
        <Box
          sx={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 340px" },
            gap: 6,
            px: 6,
            py: 4,
            alignItems: "start",
          }}
        >
          <PilotCanvas
            siteId={siteId}
            generationSlug={pilotState.generationSlug}
            isStarting={isGeneratingPilot || isFetchingBlueprint}
          />

          <SetupRail
            product={pilotState.product}
            blueprint={blueprint}
            primaryLabel={primaryLabel}
            primaryDisabled={
              !canGenerate ||
              isBusy ||
              isResumingGeneration ||
              (hasGenerated && isLoadingCollection)
            }
            onPrimary={() =>
              hasGenerated ? openAcceptPilot() : void runGeneration()
            }
            showRegenerate={hasGenerated}
            regenerateDisabled={isGeneratingPilot || isFetchingBlueprint}
            onRegenerate={() => void runGeneration()}
            pickersDisabled={isBusy}
            onChooseProduct={() => setPickingProduct(true)}
            onChooseBlueprint={() => setPickingBlueprint(true)}
          />
        </Box>
      </Box>

      <AcceptPilotModal
        open={accepting}
        onClose={() => setAccepting(false)}
        onPropagate={() => {
          void acceptPilot();
        }}
        pilotImageSrc={
          pilotState.pilotImageUrl ?? pilotState.product?.image_url
        }
        pilotImageAlt={pilotState.product?.title}
        blueprint={blueprint}
        productName={pilotState.product?.title}
        productGroup={pilotState.productGroup}
        collectionCount={groupProducts.length}
        busy={isAccepting}
      />

      <ProductPickerModal
        open={pickingProduct}
        siteId={siteId}
        filter={pilotState.taxonomyFilter}
        onClose={() => setPickingProduct(false)}
        onSelect={(product) => {
          if (isPilotRequestPending) return;
          setPilotState((prev) =>
            withPilotInput(prev, {
              product: {
                id: product.id,
                title: product.title,
                image_url: product.image_url,
              },
            }),
          );
          setPickingProduct(false);
        }}
      />
      <BlueprintPickerModal
        open={pickingBlueprint}
        siteId={siteId}
        onClose={() => setPickingBlueprint(false)}
        onSelect={(selectedBlueprint) => {
          if (isPilotRequestPending) return;
          setPilotState((prev) =>
            withPilotInput(prev, { blueprint: selectedBlueprint }),
          );
          setPickingBlueprint(false);
        }}
      />
    </>
  );
}
